import { CoreImageView, CoreTextView, TCoreButtonViewProps, CoreLayoutView, TCoreLayoutStyleProps } from '@core/widgets';
import { useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import React from 'react';
import { TCardView } from '../../types/data';
import { ECardView } from '../../types/enums';
import { TextStyle } from '../../types/styles';
import { getHref } from '../Image/imageUtils';
import { VisualView } from '../Image/VisualView';
import styles from './cardView.module.css';

const chunkString = (str: string | number): Array<string> => {
    if (!str) {
        return [];
    }
    return (
        str
            .toString()
            .replace(/\*/g, '•')
            .match(/.{1,4}/g) || []
    );
};

export const CardViewWidget = ({ widget }: TWidgetProps<TCardView>) => {
    const { bankImage, bankTitle, pan, accountNumber, expirationDate, paymentSystem, status, messages } = widget.data ?? {};

    const { isDisabled, isHidden } = useBehavior(widget);

    const isDisabledStatus = status === ECardView.DISABLED || status === ECardView.ACCOUNT_NUMBER_DISABLED || isDisabled;
    const isAccountView = status === ECardView.ACCOUNT_NUMBER_ACTIVE || status === ECardView.ACCOUNT_NUMBER_DISABLED;

    const cardStyleProps: TCoreLayoutStyleProps = {
        width: 327,
        height: 218,
        borderColor: 'var(--color-grey_16_lc)',
        borderRadius: 16,
        lineY: 'space_between',
        lineX: 'stretch',
        borderStroke: 1,
        margin: widget.options?.margin,
        padding: {
            top: 24,
            right: 24,
            bottom: 24,
            left: 24,
        },
        disabled: {
            borderColor: 'var(--color-grey_8_lc)',
        },
    };

    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];

    return (
        <CoreLayoutView
            uid={widget.uid}
            widget={widget as unknown as TCoreButtonViewProps['widget']}
            isDisabled={isDisabledStatus}
            isHide={isHidden}
            styleName="cardViewContainer"
            parentStyleNames={parentStyleNames}
            styleProps={cardStyleProps}
            fullWidth
        >
            <div data-test={'bankNameAndLogo_' + widget.uid} className={styles.header}>
                {/* Header: Bank Icon + Title */}
                <VisualView
                    iconName={bankImage}
                    defaultIconName={isAccountView ? 'ic_bank' : 'ic_card'}
                    iconVariant="circle"
                    iconHierarchy="secondary"
                    parentStyleNames={[...parentStyleNames, 'cardViewBankIcon']}
                    isDisabled={isDisabledStatus}
                    margin={{ right: 16 }}
                />
                <CoreTextView
                    uid="cardViewBankTitle"
                    text={bankTitle}
                    isDisabled={isDisabledStatus}
                    styleName="cardViewBankTitle"
                    parentStyleNames={parentStyleNames}
                    styleProps={{
                        ...TextStyle.main,
                        main: 'var(--color-black)',
                        maxLines: 2,
                        disabled: {
                            main: 'var(--color-grey_40_lc)',
                        },
                    }}
                />
            </div>
            {isAccountView ? (
                /* Account Mode */
                <div data-test={'accountView_' + widget.uid} className={styles.accountView}>
                    <CoreTextView
                        uid="cardViewAccountLabel"
                        text={messages?.accountNumberPlaceholder}
                        styleName="cardViewAccountLabel"
                        parentStyleNames={parentStyleNames}
                        styleProps={{
                            ...TextStyle.secondary,
                            main: 'var(--color-grey_72_lc)',
                            disabled: {
                                main: 'var(--color-grey_40_lc)',
                            },
                        }}
                        isDisabled={isDisabledStatus}
                    />
                    <CoreTextView
                        uid="cardViewAccountNumber"
                        text={chunkString(accountNumber).join(' ')}
                        styleName="cardViewAccountNumber"
                        parentStyleNames={parentStyleNames}
                        styleProps={{
                            ...TextStyle.main,
                            main: 'var(--color-black)',
                            disabled: {
                                main: 'var(--color-grey_40_lc)',
                            },
                        }}
                        isDisabled={isDisabledStatus}
                    />
                </div>
            ) : (
                /* Card Mode */
                <div data-test={'panView_' + widget.uid} className={styles.panView}>
                    <div data-test={'pan_' + widget.uid} className={styles.container}>
                        {pan &&
                            chunkString(pan).map((str) => (
                                <CoreTextView
                                    key={str}
                                    text={str}
                                    as="span"
                                    styleName="cardViewCardNumber"
                                    parentStyleNames={parentStyleNames}
                                    styleProps={{
                                        ...TextStyle.main,
                                        main: 'var(--color-black)',
                                        disabled: {
                                            main: 'var(--color-grey_40_lc)',
                                        },
                                    }}
                                    isDisabled={isDisabledStatus}
                                />
                            ))}
                    </div>
                    <div data-test={'expDate_' + widget.uid} className={styles.footer}>
                        <CoreTextView
                            uid="cardViewExpDate"
                            text={`${messages?.expDate ? messages.expDate.toUpperCase() : ''} ${expirationDate || ''}`.trim()}
                            styleName="cardViewExpDate"
                            parentStyleNames={parentStyleNames}
                            styleProps={{
                                ...TextStyle.secondary,
                                main: 'var(--color-grey_72_lc)',
                                disabled: {
                                    main: 'var(--color-grey_40_lc)',
                                },
                            }}
                            isDisabled={isDisabledStatus}
                        />
                        {paymentSystem && (
                            <CoreImageView
                                src={getHref(paymentSystem)}
                                defaultIcon={getHref('paysystem_mastercard')}
                                parentStyleNames={[...parentStyleNames, 'cardViewPaymentIcon']}
                                styleProps={{
                                    width: 'auto',
                                    height: 'auto',
                                    iconColor: 'var(--color-grey_40_lc)',
                                }}
                                isDisabled={isDisabledStatus}
                            />
                        )}
                    </div>
                </div>
            )}
        </CoreLayoutView>
    );
};
